import React, { useState, useEffect } from 'react';
import { workingDaysApi } from '../lib/api';
import { Clock, Save } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';

const ALL_DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

const WorkingDaysPage = () => {
  const [workingDays, setWorkingDays] = useState(['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday']);
  const [rescheduling, setRescheduling] = useState('next_working_day');
  const [loading, setLoading] = useState(true);

  useEffect(() => { load(); }, []);

  const load = async () => {
    setLoading(true);
    try {
      const res = await workingDaysApi.get();
      setWorkingDays(res.data.working_days || ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday']);
      setRescheduling(res.data.repayment_rescheduling || 'next_working_day');
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const toggleDay = (day) => {
    setWorkingDays(prev => prev.includes(day) ? prev.filter(d => d !== day) : [...prev, day]);
  };

  const handleSave = async () => {
    try {
      await workingDaysApi.update({ working_days: workingDays, repayment_rescheduling: rescheduling });
      toast.success('Working days updated');
    } catch (e) { toast.error('Failed to update'); }
  };

  return (
    <div className="space-y-6 fade-in" data-testid="working-days-page">
      <div className="flex items-center gap-2 text-xs text-gray-500 mb-1">
        <span>Admin</span><span>/</span><span>Organization</span><span>/</span><span className="text-[#1f1f1f] font-medium">Working Days</span>
      </div>
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#C5A059] mb-1">CONFIGURATION</p>
          <h1 className="font-display text-2xl font-bold text-[#1f1f1f]">Working Days</h1>
        </div>
        <Button onClick={handleSave} className="bg-[#1f1f1f] hover:bg-[#333333] text-white" data-testid="save-working-days-btn">
          <Save className="w-4 h-4 mr-2" /> Save Changes
        </Button>
      </div>

      {loading ? <div className="text-center py-8 text-gray-400">Loading...</div> : (
        <div className="bg-white border border-gray-200 p-6 space-y-6">
          <div>
            <Label className="text-sm font-semibold text-gray-700 mb-3 block">Select Working Days</Label>
            <div className="grid grid-cols-7 gap-2">
              {ALL_DAYS.map(day => (
                <button
                  key={day}
                  onClick={() => toggleDay(day)}
                  className={`py-3 px-2 text-sm font-medium border transition-colors ${
                    workingDays.includes(day)
                      ? 'bg-[#1f1f1f] text-white border-[#1f1f1f]'
                      : 'bg-white text-gray-600 border-gray-200 hover:border-gray-400'
                  }`}
                  data-testid={`day-${day.toLowerCase()}`}
                >
                  {day}
                </button>
              ))}
            </div>
          </div>

          <div className="max-w-md">
            <Label className="text-sm font-semibold text-gray-700 mb-2 block">Repayment Rescheduling Strategy</Label>
            <p className="text-xs text-gray-500 mb-2">When a repayment falls on a non-working day, how should it be rescheduled?</p>
            <Select value={rescheduling} onValueChange={setRescheduling}>
              <SelectTrigger data-testid="reschedule-strategy"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="next_working_day">Move to next working day</SelectItem>
                <SelectItem value="previous_working_day">Move to previous working day</SelectItem>
                <SelectItem value="next_repayment_date">Move to next repayment date</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      )}
    </div>
  );
};

export default WorkingDaysPage;
